import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { IconTime } from '@americanexpress/dls-icons';
import { Label, SelectNative, SelectNativeOption } from '@americanexpress/dls-react';
import { useMediaBreakpoint, getHourLabels } from '@americanexpress/consumer-travel-base';
import { getStateKey } from '../../selectors/commonSelectors';
import {
  getDefaultTime,
  getHasAnytime,
  getIs24Hour,
  getMinutesIntervalPerOption,
  getEndTime,
  getStartTime,
} from '../../selectors/timeSelectors';
import styles from './TimeDropdown.scss';
import { useTimeDispatch, useTimeSelector } from '../../context/timeContext';
import { useDateSelector } from '../../context/dateContext';
import ANYTIME from '../../constants/timeDropdownConstants';
import { getEndDate, getStartDate } from '../../selectors/dateSelectors';
import { isValidTime } from '../../utils/timeUtils';
import { END_TIME } from '../../constants/dateConstants';
import { useIsHomePageVariantHook } from '../../hooks/useIsHomePageVariantHook';

const TimeDropdown = ({
  stateKey,
  ariaLabel,
  onChangeAction,
  showPastHoursOfDay,
  className,
}) => {
  const { formatMessage } = useIntl();
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);
  const startTime = useTimeSelector(getStartTime);
  const endTime = useTimeSelector(getEndTime);
  const dispatch = useTimeDispatch();
  const is24Hour = useTimeSelector(getIs24Hour);
  const hasAnytime = useTimeSelector(getHasAnytime);
  const minutesIntervalPerOption = useTimeSelector(getMinutesIntervalPerOption);
  const defaultTime = useTimeSelector(getDefaultTime);
  const stateValueFromContext = useTimeSelector(getStateKey(stateKey));
  const stateValue = stateValueFromContext || (hasAnytime ? ANYTIME : defaultTime);
  const isMobile = useMediaBreakpoint('sm-down');

  const [options, setOptions] = useState([]);

  const handleChange = ({ target: { value: newValue } }) => {
    dispatch(onChangeAction(newValue));
  };

  const anytimeLabel = formatMessage({ id: 'anytime' });
  const currentStartTime = hasAnytime ? startTime : startTime || defaultTime;

  useEffect(() => {
    setOptions(
      getHourLabels(anytimeLabel, {
        is24Hour,
        minutesIntervalPerOption,
        hasAnytime,
        showPastHoursOfDay,
      })
    );
  }, [
    showPastHoursOfDay,
    anytimeLabel,
    hasAnytime,
    is24Hour,
    minutesIntervalPerOption,
  ]);

  useEffect(() => {
    if (
      !isValidTime({
        startDate,
        endDate,
        startTime: currentStartTime,
        endTime,
        value: stateValue,
        stateKey,
      })
      && stateKey === END_TIME
    ) {
      dispatch(onChangeAction(currentStartTime));
    }
  }, [startDate, endDate, currentStartTime]);

  const isVariant = useIsHomePageVariantHook();

  return (
    <div className={clsx(styles.timeDropdownWrapper, className, 'flex flex-wrap flex-column')}>
      {
        isVariant ? (
          <Label htmlFor={`date-input-${stateKey}`} className="margin-1-b">{formatMessage({ id: ariaLabel })}</Label>
        ) : (
          <div className="flex flex-align-center">
            <IconTime
              title="Time Icon"
              titleId={`${stateKey}-timeIcon`}
              color="neutral"
              size="sm"
            />
            <label htmlFor={`date-input-${stateKey}`} className="body color-text-subtle margin-0">
              {formatMessage({ id: ariaLabel })}
            </label>
          </div>
        )
      }
      <SelectNative
        value={stateValue}
        onChange={handleChange}
        aria-label={ariaLabel}
        id={`date-input-${stateKey}`}
        fluid={isMobile}
        className={clsx(styles.timeDropdown, 'flex-item-grow')}
      >
        {options.map(({ label, value }) => (
          <SelectNativeOption
            key={value}
            value={value}
            aria-disabled={
              !isValidTime({
                startDate,
                endDate,
                startTime: currentStartTime,
                value,
                stateKey,
              })
            }
          >
            {label}
          </SelectNativeOption>
        ))}
      </SelectNative>
    </div>
  );
};

TimeDropdown.propTypes = {
  stateKey: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onChangeAction: PropTypes.func.isRequired,
  showPastHoursOfDay: PropTypes.bool,
  className: PropTypes.string,
};

TimeDropdown.defaultProps = {
  showPastHoursOfDay: true,
  className: '',
};

export { TimeDropdown as TestableTimeDropdown };

export default TimeDropdown;
